/* Copyright (c) 2021-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useReducer, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { FormProvider, useForm } from "react-hook-form";
import { When } from "react-if";
import { FlexContainer, theme } from "@nordcloud/gnui";
import { isNil, isNonEmpty } from "~/tools";
import { Item } from "~/types";
import {
  baselineTagToShow,
  getBaselineSourceIndex,
  getFlatPlanEntities,
  getParamsForDependentEntity,
  isPatchOrScanEntity,
} from "~/utils/baselinePatching";
import { ActionBatch } from "~/views/plans/components/ActionBatch";
import { ActionItemWrap } from "~/views/plans/styled";
import { PlanErrors, PlanField } from "../../../constants";
import { usePlanWizard } from "../../../PlanProvider";
import { PlanEntity, SelectedPlanEntity } from "../../../types";
import { useStepState } from "../../StepProvider";
import { ActionItemEdit } from "../ActionItemEdit";
import { AddActionsSidebar } from "../AddAction/AddActionsSidebar";
import { createNewAction } from "../AddAction/helpers";
import { defaultState } from "../constant";
import { ExecutionMethod, State } from "../types";
import { DraggableBatchActions } from "./DraggableBatchActions";
import { EditableActionName } from "./EditableActionName";

type Props<T> = {
  items: T[];
  onDelete: (item: T) => void;
  nextStep?: () => void;
};

export function DraggablePlanEntitiesList<T extends PlanEntity>({
  items,
  onDelete,
  nextStep,
}: Props<T>) {
  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({
      ...data,
      ...partialData,
    }),
    defaultState
  );

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const { setPlanData, planData, setSelectedEntity } = usePlanWizard();

  const {
    stepState: { entities, selectedEntity: selectedPlanEntity },
    updateStepState,
  } = useStepState();

  const handleDelete = (item: T) => {
    const indexOfDeletedEntity = entities.findIndex(
      (entity) => entity.listId === item.listId
    );

    const restOfPlanEntities = entities.filter(
      (entity) => entity.listId !== item.listId
    );

    const selectedEntityAfterDelete =
      restOfPlanEntities[Math.max(indexOfDeletedEntity - 1, 0)];

    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: { planEntities: restOfPlanEntities },
    }));

    updateStepState({
      entities: restOfPlanEntities,
      selectedEntity: selectedEntityAfterDelete,
    });
    setSelectedEntity?.(selectedEntityAfterDelete?.listId);
    onDelete(item);
  };

  const setPlanErrors = (planError: PlanErrors) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.ERRORS]: [
        ...new Set([...(prevPlanData[PlanField.ERRORS] ?? []), planError]),
      ],
    }));
  };

  const clearPlanError = (planError: PlanErrors) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.ERRORS]: prevPlanData[PlanField.ERRORS]?.filter(
        (errors) => errors !== planError
      ),
    }));
  };

  const handlePlanErrors = () => {
    if (Object.keys(errors).length > 0) {
      setPlanErrors(PlanErrors.PLAN_ACTION_NAMES);
    } else {
      clearPlanError(PlanErrors.PLAN_ACTION_NAMES);
    }
  };

  const formMethods = useForm();

  const {
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = formMethods;

  const submit = () => {
    nextStep?.();
  };

  function isItem(action: SelectedPlanEntity): action is Item {
    return action.id !== undefined;
  }

  const handleSelectBatch = (item: T) => {
    updateStepState({
      selectedEntity: {
        ...item,
      },
    });
    setSelectedEntity?.(item.listId);
  };

  const addNewBatchActions = (actions: SelectedPlanEntity[]) => {
    const actionItems = actions.map((actionItem) => {
      const newAction = createNewAction(actionItem, selectedPlanEntity?.id);
      const flatEntities = getFlatPlanEntities(entities);
      const baselineIndex = getBaselineSourceIndex(flatEntities);

      if (baselineIndex !== -1 && isPatchOrScanEntity(newAction)) {
        return {
          ...newAction,
          inputParameters: getParamsForDependentEntity(
            newAction,
            flatEntities[baselineIndex].inputParameters
          ),
        };
      }

      return newAction;
    });

    const updatedEntities =
      entities?.map((entity) => {
        if (entity.listId === selectedPlanEntity?.listId) {
          return {
            ...entity,
            batchActions: [...(entity.batchActions ?? []), ...actionItems],
          };
        }
        return entity;
      }) ?? [];

    updateStepState({
      selectedEntity: actionItems[updatedEntities?.length - 1],
      entities: updatedEntities,
    });

    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        ...prevPlanData[PlanField.PLAN_SETTINGS],
        planEntities: updatedEntities,
      },
    }));

    updateState({ isSidebarOpen: false, selectedActions: [] });
  };

  const flatEntities = useMemo(
    () =>
      getFlatPlanEntities(
        planData?.[PlanField.PLAN_SETTINGS]?.planEntities || []
      ),
    [planData]
  );

  return (
    <>
      <FlexContainer as="ul" direction="column" alignItems="stretch">
        <form id="planSettingsForm" onSubmit={handleSubmit(submit)}>
          {items?.map((i, index) => {
            const entityListId = i.listId;
            if (isNil(entityListId)) {
              return null;
            }

            const baselineTag = baselineTagToShow(i, flatEntities);
            const item = {
              ...i,
              icon: "dragAndDrop",
              editableName: (
                <FormProvider {...formMethods}>
                  <EditableActionName
                    item={i}
                    isEditing={editingItemId === entityListId}
                    tag={baselineTag}
                    onEditClick={() => setEditingItemId(entityListId)}
                    handlePlanErrors={handlePlanErrors}
                    onEditComplete={() => setEditingItemId(null)}
                  />
                </FormProvider>
              ),
            };
            const actionBatch = isNonEmpty(item.batchActions)
              ? item
              : undefined;

            return (
              <Draggable
                key={entityListId}
                draggableId={entityListId}
                index={index}
              >
                {(provided) => {
                  if (isNonEmpty(item.batchActions)) {
                    return (
                      <div css={{ paddingTop: `${theme.spacing.spacing03}` }}>
                        <ActionBatch
                          key={index}
                          id={item.id}
                          name={item.name}
                          editableName={item.editableName}
                          onDelete={() => handleDelete(item)}
                          onAddAction={() => {
                            updateState({ isSidebarOpen: true });
                          }}
                          onClick={() => handleSelectBatch(item)}
                          active={selectedPlanEntity?.listId === entityListId}
                          {...{ provided, item }}
                        >
                          <DraggableBatchActions
                            actions={item.batchActions.filter(isItem) ?? []}
                            actionBatch={actionBatch}
                          />
                        </ActionBatch>
                      </div>
                    );
                  }
                  return (
                    <ActionItemWrap>
                      <ActionItemEdit
                        isActive={selectedPlanEntity?.listId === entityListId}
                        aria-label={item.name}
                        {...{ item }}
                        {...{ provided }}
                        editableName={item.editableName}
                        onMouseDown={() => {
                          setSelectedEntity?.(entityListId);
                          updateStepState({ selectedEntity: item });
                        }}
                        onDelete={() => {
                          clearErrors(entityListId);
                          handlePlanErrors();
                          handleDelete(item);
                        }}
                      />
                    </ActionItemWrap>
                  );
                }}
              </Draggable>
            );
          })}
        </form>
      </FlexContainer>
      <When condition={state.isSidebarOpen}>
        <AddActionsSidebar
          executionMethod={ExecutionMethod.batch}
          {...{ state, updateState }}
          isAddingNewEntity={false}
          onApply={addNewBatchActions}
        />
      </When>
    </>
  );
}
